/**
 * URLs for the Learn section, in the language being read.
 *
 * The paths used to be half-translated in both directions: an English reader
 * got "/learn/ruta/viaje-del-cafe" and a Spanish one got "/learn/..." for a
 * section called "Aprender". Every segment now follows the reading language,
 * and every combination still resolves, so links shared before this change —
 * or shared by a reader in the other language — keep working.
 *
 * The slug follows too. Every article, route and lens carries a Spanish slug
 * — the canonical id, used by `related` arrays and every internal lookup —
 * and an English one that is only ever a URL. Lookups accept either, so no
 * link that ever worked stops working.
 */

const SEGMENTS = {
  learn: { es: "aprender", en: "learn" },
  route: { es: "ruta", en: "route" },
  lens: { es: "lente", en: "lens" },
} as const;

type SegmentKind = keyof typeof SEGMENTS;

const segment = (kind: SegmentKind, isSpanish: boolean) =>
  isSpanish ? SEGMENTS[kind].es : SEGMENTS[kind].en;

/** Both spellings of a segment, for registering router paths. */
export const segmentVariants = (kind: SegmentKind): string[] => [
  SEGMENTS[kind].es,
  SEGMENTS[kind].en,
];

export const learnHref = (isSpanish: boolean) =>
  `/${segment("learn", isSpanish)}`;

/** Anything with a slug in both languages. */
export interface Slugged {
  slug: string;
  slugEn: string;
}

const slugFor = (item: Slugged, isSpanish: boolean) =>
  isSpanish ? item.slug : item.slugEn;

export const topicHref = (isSpanish: boolean, topic: Slugged) =>
  `${learnHref(isSpanish)}/${slugFor(topic, isSpanish)}`;

export const lensHref = (isSpanish: boolean, lens: Slugged) =>
  `${learnHref(isSpanish)}/${segment("lens", isSpanish)}/${slugFor(lens, isSpanish)}`;

/**
 * A route, optionally as a thread the reader is following: the country pages
 * it links to carry the route in a query parameter so they can show where
 * the reader is in the story.
 */
export const routeHref = (isSpanish: boolean, route: Slugged) =>
  `${learnHref(isSpanish)}/${segment("route", isSpanish)}/${slugFor(route, isSpanish)}`;

/** The query parameter that carries the route a country page arrived from. */
export const ROUTE_PARAM = { es: "ruta", en: "route" } as const;

export const countryHref = (
  isSpanish: boolean,
  displayName: string,
  route?: Slugged
) => {
  const path = `/countries/${displayName}`;
  if (!route) return path;
  const key = isSpanish ? ROUTE_PARAM.es : ROUTE_PARAM.en;
  return `${path}?${key}=${encodeURIComponent(slugFor(route, isSpanish))}`;
};

/** Reads the route parameter under either spelling. */
export const readRouteParam = (params: URLSearchParams) =>
  params.get(ROUTE_PARAM.en) ?? params.get(ROUTE_PARAM.es);
